import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { Link, useNavigate } from "react-router-dom";
import Button from "@mui/material/Button";

// Icons
import { RxDashboard } from "react-icons/rx";
import { FaAngleRight, FaAngleDown } from "react-icons/fa";
import { IoCreateOutline, IoDocumentsOutline } from "react-icons/io5";
import { CiCircleAlert, CiBank, CiSettings } from "react-icons/ci";
import { LuWarehouse } from "react-icons/lu";
import { MdLocalOffer } from "react-icons/md";
import { TbReport } from "react-icons/tb";
import { GoMilestone } from "react-icons/go";
import { MdSupportAgent } from "react-icons/md";
import { RiGroupLine } from "react-icons/ri";
import { CgProfile } from "react-icons/cg";
import { GiBrain } from "react-icons/gi";
import { MdIntegrationInstructions } from "react-icons/md";
import { LogOut } from "lucide-react";

import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { useMyContext } from "../../../context/MyContext";
import "../../css/custom-scrollbar.css";
import { useAuth } from "../../../routes/Auth";

const Sidebar = () => {
  const { profile_flag, isToggleSidebar, setIsToggleSidebar } =
    useMyContext();

  const [activeTab, setActiveTab] = useState(-1);
  const [activeSubTab, setActiveSubTab] = useState(-1);

  const navigate = useNavigate();
  const { logout } = useAuth();

  const sidebarRef = useRef(null);
  const userRole = localStorage.getItem("role");

  // ============================================================
  // MENU ITEMS
  // ============================================================

  let menuItems = [
    {
      title: "Dashboard",
      icon: <RxDashboard />,
      href: "/dashboard",
      submenu: [],
    },

    {
      title: "Shipments",
      icon: <IoCreateOutline />,
      submenu: [
        {
          title: "Forward Shipment",
          href: "/shipments/forward",
        },
        {
          title: "Track Shipments",
          href: "/shipments/tracking",
        },
        {
          title: "View Shipments",
          href: "/shipments/all",
        },
      ],
    },

    {
      title: "Warehouse",
      icon: <LuWarehouse />,
      submenu: [
        {
          title: "Add Warehouse",
          href: "/warehouse/add",
        },
        {
          title: "All Warehouses",
          href: "/warehouse/all",
        },
      ],
    },

    {
      title: "Integration",
      icon: <MdIntegrationInstructions />,
      href: "/intregration",
      submenu: [],
    },

    {
      title: "Disputes",
      icon: <CiCircleAlert />,
      submenu: [
        {
          title: "NDR",
          href: "/disputes/NDR",
        },
        {
          title: "Weight Disputes",
          href: "/disputes/weight",
        },
        {
          title: "Weight Upload",
          href: "/disputes/wightUpload",
        },
        {
          title: "All Weight Disputes",
          href: "/disputes/allWeightDisputes",
        },
      ],
    },

    {
      title: "Payments",
      icon: <CiBank />,
      submenu: [
        {
          title: "Remmitance",
          href: "/payments/remmitance",
        },
        {
          title: "RemmitanceList",
          href: "/payments/remmitanceList",
        },
        {
          title: "Wallet Balance",
          href: "/payments/walletbalance",
        },
      ],
    },

    {
      title: "Label",
      icon: <IoDocumentsOutline />,
      href: "/labelCustomized",
      submenu: [],
    },

    {
      title: "Services",
      icon: <GoMilestone />,
      href: "/services",
      submenu: [],
    },

    {
      title: "Offers",
      icon: <MdLocalOffer />,
      submenu: [
        {
          title: "Set Offer",
          href: "/offer/setoffer",
        },
        {
          title: "All offers",
          href: "/offer/viewAllOffers",
        },
      ],
    },

    {
      title: "Customers",
      icon: <RiGroupLine />,
      submenu: [
        {
          title: "ViewAllCustomer",
          href: "/customers/viewCustomer",
        },
        {
          title: "KYC Pending",
          href: "/customers/kycPending",
        },
      ],
    },

    {
      title: "Preship",
      icon: <GiBrain />,
      submenu: [
        {
          title: "Rate Calculator",
          href: "/settings/rateCalculator",
        },
        {
          title: "Servicable Pincodes",
          href: "/settings/pincode",
        },
        {
          title: "Rate Card",
          href: "/settings/rateCard",
        },
        {
          title: "Add Pincodes",
          href: "/settings/addPincode",
        },
        {
          title: "Set Rate",
          href: "/settings/setRate",
        },
      ],
    },

    {
      title: "Reports",
      icon: <TbReport />,
      href: "/reports",
      submenu: [],
    },

    {
      title: "Help",
      icon: <MdSupportAgent />,
      href: "/help",
      submenu: [],
    },

    {
      title: "Setting",
      icon: <CiSettings />,
      submenu: [
        {
          title: "Add Employee",
          href: "/addEmployee",
        },
        {
          title: "All Employee",
          href: "/allEmployee",
        },
      ],
    },

    {
      title: "Profile",
      icon: <CgProfile />,
      href: "/profile",
      submenu: [],
    },
  ];

  // ============================================================
  // ROLE BASED MENU
  // ============================================================

  if (userRole !== "admin") {
    menuItems.forEach((item) => {
      if (item.title === "Disputes") {
        item.submenu = item.submenu.filter(
          (subItem) =>
            subItem.title !== "Weight Upload" &&
            subItem.title !== "All Weight Disputes"
        );
      }

      if (item.title === "Preship") {
        item.submenu = item.submenu.filter(
          (subItem) =>
            subItem.title !== "Set Rate" &&
            subItem.title !== "Add Pincodes"
        );
      }

      if (item.title === "Payments") {
        item.submenu = item.submenu.filter(
          (subItem) => subItem.title !== "RemmitanceList"
        );
      }
    });

    menuItems = menuItems.filter(
      (item) =>
        item.title !== "Customers" &&
        item.title !== "Offers" &&
        item.title !== "Setting"
    );
  } else {
    menuItems.forEach((item) => {
      if (item.title === "Preship") {
        item.submenu = item.submenu.filter(
          (subItem) => subItem.title !== "Rate Card"
        );
      }

      if (item.title === "Disputes") {
        item.submenu = item.submenu.filter(
          (subItem) => subItem.title !== "Weight Disputes"
        );
      }

      if (item.title === "Payments") {
        item.submenu = item.submenu.filter(
          (subItem) => subItem.title !== "Remmitance"
        );
      }
    });

    menuItems = menuItems.filter(
      (item) => item.title !== "Label"
    );
  }

  // ============================================================
  // ACTIVE ROUTE
  // ============================================================

  useEffect(() => {
    const currentRoute = window.location.pathname;

    menuItems.forEach((item, index) => {
      if (item.href === currentRoute) {
        setActiveTab(index);
        setActiveSubTab(-1);
      }

      if (item.submenu?.length > 0) {
        item.submenu.forEach((subItem, subIndex) => {
          if (subItem.href === currentRoute) {
            setActiveTab(index);
            setActiveSubTab(subIndex);
          }
        });
      }
    });
  }, []);

  // ============================================================
  // MENU TOGGLE
  // ============================================================

  const toggleSubmenu = useCallback(
    (index, href) => {
      if (profile_flag === "0" && userRole !== "admin") {
        toast.error(
          "Please Contact to the Support Team, Your KYC is Pending"
        );
        return;
      }

      if (href) {
        navigate(href);
        return;
      }

      setActiveTab((prev) =>
        prev === index ? -1 : index
      );

      setActiveSubTab(-1);
    },
    [profile_flag, userRole, navigate]
  );

  // ============================================================
  // MOBILE OUTSIDE CLICK
  // ============================================================

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target) &&
        window.innerWidth <= 768
      ) {
        setIsToggleSidebar(false);
      }
    };

    if (isToggleSidebar) {
      document.addEventListener(
        "mousedown",
        handleClickOutside
      );
    }

    return () => {
      document.removeEventListener(
        "mousedown",
        handleClickOutside
      );
    };
  }, [isToggleSidebar, setIsToggleSidebar]);

  // ============================================================
  // LOGOUT
  // ============================================================

  const handleLogout = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("cus_id");
    localStorage.removeItem("token");
    localStorage.removeItem("wallet_status");
    localStorage.removeItem("profile_flag");
    localStorage.removeItem("role");
    localStorage.removeItem("isB2BMode");

    navigate("/login");

    logout();

    toast.success("Logout Successfully!");
  };

  // ============================================================
  // SIDEBAR
  // ============================================================

  return (
    <>
      {isToggleSidebar && (
        <aside
          ref={sidebarRef}
          className="
            relative
            w-64
            h-full
            flex
            flex-col
            overflow-hidden
            text-gray-700
            sidebar-glass
          "
        >
          {/* =====================================================
              LIGHT APPLE GLASS BACKGROUND
          ===================================================== */}

          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            {/* Base */}
            <div className="absolute inset-0 bg-white/65" />

            {/* Top Right Blue Glow */}
            <div
              className="
                absolute
                -top-28
                -right-24
                w-[350px]
                h-[320px]
                rounded-full
                bg-blue-200/40
                blur-[85px]
              "
            />

            {/* Top Left Pink Glow */}
            <div
              className="
                absolute
                -top-10
                -left-28
                w-[320px]
                h-[290px]
                rounded-full
                bg-pink-600/35
                blur-[10px]
              "
            />

            {/* Center Purple Glow */}
            <div
              className="
                absolute
                top-[35%]
                -right-32
                w-[380px]
                h-[360px]
                rounded-full
                bg-purple-200/30
                blur-[105px]
              "
            />

            {/* Bottom Cyan Glow */}
            <div
              className="
                absolute
                -bottom-32
                -left-20
                w-[380px]
                h-[350px]
                rounded-full
                bg-cyan-200/35
                blur-[105px]
              "
            />

            {/* Diagonal Glass Color */}
            <div
              className="
                absolute
                top-[42%]
                -left-[100px]
                w-[520px]
                h-[230px]
                rotate-[60deg]
                rounded-full
                bg-gradient-to-t
                from-pink-200/25
                via-purple-200/25
                to-cyan-200/30
                blur-[80px]
              "
            />

            {/* Frosted Glass */}
            <div
              className="
                absolute
                inset-0
                bg-white/10
                backdrop-blur-[90px]
                backdrop-saturate-[100%]
              "
            />

            {/* White Glass Light */}
            <div
              className="
                absolute
                inset-0
                bg-gradient-to-br
                from-white/70
                via-white/25
                to-gray-500/10
              "
            />

            {/* Top Reflection */}
            <div
              className="
                absolute
                top-0
                left-0
                right-0
                h-24
                bg-gradient-to-b
                from-white/80
                via-white/25
                to-transparent
              "
            />

            {/* Left Soft Reflection */}
            <div
              className="
                absolute
                left-0
                top-0
                bottom-0
                w-8
                bg-gradient-to-r
                from-white/30
                to-transparent
              "
            />

            {/* Right Border */}
            <div
              className="
                absolute
                top-0
                right-0
                bottom-0
                w-px
                bg-gray-300/40
              "
            />

            {/* Bottom Shadow */}
            <div
              className="
                absolute
                bottom-0
                left-0
                right-0
                h-20
                bg-gradient-to-t
                from-gray-900/[0.025]
                to-transparent
              "
            />
          </div>

          {/* =====================================================
              CONTENT
          ===================================================== */}

          <div className="relative z-10 flex flex-col h-full">

            {/* =================================================
                MENU CONTAINER
            ================================================= */}

            <div className="flex-1 overflow-hidden">
              <div
                className="
                  h-full
                  overflow-y-auto
                  px-3
                  py-4
                "
              >
                {/* =================================================
                    MENU
                ================================================= */}

                <ul className="space-y-1">
                  {menuItems.map((item, index) => {
                    const isActive =
                      activeTab === index;

                    return (
                      <li
                        key={index}
                        className="group"
                      >
                        {/* =================================================
                            MAIN MENU BUTTON
                        ================================================= */}

                        <Button
                          onClick={() =>
                            toggleSubmenu(
                              index,
                              item.href
                            )
                          }
                          sx={{
                            minWidth: 0,

                            fontFamily:
                              "var(--font-custom-open-sans)",

                            textTransform: "none",

                            width: "100%",

                            textAlign: "left",

                            fontWeight: 500,

                            display: "flex",

                            alignItems: "center",

                            padding: "8px 10px",

                            borderRadius: "11px",

                            position: "relative",

                            overflow: "hidden",

                            transition:
                              "all 0.3s cubic-bezier(0.16, 1, 0.3, 1)",

                            color: isActive
                              ? "#374151"
                              : "rgba(75,85,99,0.68)",

                            /*
                             * SAME GLASS EFFECT AS LOGOUT
                             * =========================================
                             */

                            background: isActive
                              ? "rgba(255,255,255,0.25)"
                              : "transparent",

                            backdropFilter: isActive
                              ? "blur(20px)"
                              : "none",

                            WebkitBackdropFilter:
                              isActive
                                ? "blur(20px)"
                                : "none",

                            border: isActive
                              ? "1px solid rgba(255,255,255,0.55)"
                              : "1px solid transparent",

                            boxShadow: isActive
                              ? "inset 0 1px 0 rgba(255,255,255,0.7), 0 5px 20px rgba(31,41,55,0.04)"
                              : "none",

                            "&:hover": {
                              transform:
                                "translateX(2px)",

                              color: "#1f2937",

                              background: isActive
                                ? "rgba(255,255,255,0.35)"
                                : "rgba(255,255,255,0.25)",

                              border:
                                "1px solid rgba(255,255,255,0.55)",

                              backdropFilter:
                                "blur(20px)",

                              WebkitBackdropFilter:
                                "blur(20px)",

                              boxShadow:
                                "inset 0 1px 0 rgba(255,255,255,0.75), 0 5px 20px rgba(31,41,55,0.04)",
                            },
                          }}
                        >
                          {/* =================================================
                              ACTIVE GLASS HIGHLIGHT
                          ================================================= */}

                          {isActive && (
                            <>
                              {/* Left glass indicator */}
                              <span
                                className="
                                  absolute
                                  left-0
                                  top-1/2
                                  -translate-y-1/2
                                  w-[3px]
                                  h-6
                                  rounded-r-full
                                  bg-gradient-to-b
                                  from-blue-400
                                  via-purple-400
                                  to-pink-400
                                  shadow-[0_0_8px_rgba(129,140,248,0.35)]
                                "
                              />

                              {/* Same soft glass shine */}
                              <span
                                className="
                                  absolute
                                  inset-0
                                  rounded-[11px]
                                  bg-gradient-to-r
                                  from-white/30
                                  via-white/10
                                  to-transparent
                                  pointer-events-none
                                "
                              />

                              {/* Top glass reflection */}
                              <span
                                className="
                                  absolute
                                  top-0
                                  left-2
                                  right-2
                                  h-px
                                  bg-white/80
                                  pointer-events-none
                                "
                              />
                            </>
                          )}

                          {/* =================================================
                              ICON
                          ================================================= */}

                          <span
                            className={`
                              relative
                              z-10
                              flex
                              items-center
                              justify-center
                              w-7
                              text-[17px]
                              transition-all
                              duration-300
                              ${
                                isActive
                                  ? "text-gray-700"
                                  : "text-gray-400 group-hover:text-gray-700"
                              }
                            `}
                          >
                            {item.icon}
                          </span>

                          {/* =================================================
                              TITLE
                          ================================================= */}

                          <span
                            className={`
                              relative
                              z-10
                              flex-1
                              text-left
                              text-[12px]
                              font-medium
                              transition-colors
                              duration-300
                              ${
                                isActive
                                  ? "text-gray-800"
                                  : "text-gray-600 group-hover:text-gray-800"
                              }
                            `}
                          >
                            {item.title}
                          </span>

                          {/* =================================================
                              ARROW
                          ================================================= */}

                          {item.submenu.length > 0 && (
                            <span
                              className={`
                                relative
                                z-10
                                text-[9px]
                                mr-1
                                transition-all
                                duration-300
                                ${
                                  isActive
                                    ? "text-gray-500 rotate-180"
                                    : "text-gray-400 group-hover:text-gray-600"
                                }
                              `}
                            >
                              {isActive ? (
                                <FaAngleDown />
                              ) : (
                                <FaAngleRight />
                              )}
                            </span>
                          )}
                        </Button>

                        {/* =================================================
                            SUBMENU
                        ================================================= */}

                        {item.submenu.length > 0 &&
                          isActive && (
                            <div
                              className="
                                relative
                                ml-5
                                mt-1
                                mb-1
                              "
                            >
                              <div
                                className="
                                  relative
                                  rounded-xl
                                  py-1.5
                                  px-1.5
                                  overflow-hidden
                                  bg-white/30
                                  backdrop-blur-2xl
                                  backdrop-saturate-[160%]
                                  border
                                  border-white/65
                                  shadow-[inset_0_1px_0_rgba(255,255,255,0.9),0_8px_25px_rgba(31,41,55,0.04)]
                                "
                              >
                                {/* Submenu Glass */}
                                <div
                                  className="
                                    absolute
                                    inset-0
                                    bg-gradient-to-br
                                    from-white/45
                                    via-transparent
                                    to-white/10
                                    pointer-events-none
                                  "
                                />

                                <ul
                                  className="
                                    relative
                                    z-10
                                    space-y-0.5
                                  "
                                >
                                  {item.submenu.map(
                                    (
                                      subItem,
                                      subIndex
                                    ) => {
                                      const isSubActive =
                                        activeSubTab ===
                                        subIndex;

                                      return (
                                        <li
                                          key={
                                            subIndex
                                          }
                                        >
                                          <Link
                                            to={
                                              subItem.href
                                            }
                                            onClick={() =>
                                              setActiveSubTab(
                                                subIndex
                                              )
                                            }
                                            className={`
                                              flex
                                              items-center
                                              px-3
                                              py-2
                                              rounded-lg
                                              text-[10px]
                                              font-medium
                                              transition-all
                                              duration-300
                                              ${
                                                isSubActive
                                                  ? "text-gray-800 bg-white/65 border border-white/80 shadow-[0_3px_10px_rgba(31,41,55,0.05)]"
                                                  : "text-gray-500 hover:text-gray-800 hover:bg-white/40"
                                              }
                                            `}
                                          >
                                            {/* Sub Active Dot */}

                                            {isSubActive && (
                                              <span
                                                className="
                                                  w-1
                                                  h-1
                                                  rounded-full
                                                  bg-gradient-to-r
                                                  from-blue-400
                                                  to-purple-400
                                                  mr-2
                                                  shadow-[0_0_5px_rgba(129,140,248,0.5)]
                                                "
                                              />
                                            )}

                                            <span>
                                              {
                                                subItem.title
                                              }
                                            </span>
                                          </Link>
                                        </li>
                                      );
                                    }
                                  )}
                                </ul>
                              </div>
                            </div>
                          )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>

            {/* =================================================
                LOGOUT
            ================================================= */}

            <div
              className="
                px-3
                pb-3
                pt-2
              "
            >
              {/* Separator */}

              <div
                className="
                  h-px
                  bg-gray-300/35
                  mb-3
                "
              />

              <button
                onClick={handleLogout}
                className="
                  group
                  relative
                  w-full
                  flex
                  items-center
                  justify-center
                  gap-2.5
                  px-3
                  py-2.5
                  rounded-xl
                  text-left
                  text-[11px]
                  font-medium
                  text-red-400
                  bg-white/25
                  border
                  border-white/55
                  backdrop-blur-xl
                  shadow-[inset_0_1px_0_rgba(255,255,255,0.7)]
                  transition-all
                  duration-300
                  hover:bg-red-50/55
                  hover:border-red-200/60
                  hover:text-red-500
                  hover:shadow-[0_5px_20px_rgba(239,68,68,0.08)]
                  active:scale-[0.98]
                "
              >
                {/* Logout Icon */}

                <div
                  className="
                    flex
                    items-center
                    justify-center
                    w-7
                    h-7
                    rounded-lg
                    bg-red-50/70
                    border
                    border-red-100/70
                    text-red-400
                    transition-all
                    duration-300
                    group-hover:bg-red-100/80
                  "
                >
                  <LogOut size={13} />
                </div>

                <span>
                  Logout
                </span>
              </button>
            </div>
          </div>
        </aside>
      )}
    </>
  );
};

export default Sidebar;